import React, { useContext, useEffect, useState } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { Button } from 'semantic-ui-react';
import PreAuthFooter from './PreAuthFooter';
import PreAuthHeader from './PreAuthHeader';
import PreAuthPageContext, { IPreAuthItemContext } from '../../Contexts/PreAuthPageContext/PreAuthPageContext';
import Spinner from '../../components/spinner/Spinner';
import styles from './PreAuthenticationDetails.module.scss';
import CreditCardI4GoIFrame, { AdditionalCardFields } from '../../components/creditCardI4GoIFrame/CreditCardI4GoIFrame';
import MenuItemsPageContext, { IMenuItemContext } from '../../Contexts/MenuItemsPageContext/MenuItemsPageContext';
import { I4goIdResponse, IAccessDetailsPostData, WalletsLoaded } from './models/PreAuthModel';
import { getAuthorizeError, getInvalidCardError, getTimeoutError } from './labels';
import ApiErrorModal from '../../components/errormodal/ApiErrorModal';
import { useNavigate, useParams } from 'react-router-dom';
import OrderDetailPageContext, {
	IOrderDetailContext,
} from '../../Contexts/OrderDetailPageContext/OrderDetailPageContext';
import { constructPlaceOrderRequestBody } from '../../utils/orderUtils';
import * as labels from '../orderDetails/labels';
import ErrorModal from '../../components/errormodal/ErrorModal';
import ViewTicketDetailPageContext, {
	IViewTicketDetailPageContext,
} from '../../Contexts/ViewTicketPageContext/ViewTicketPageContext';
import LegalConsentModal from '../../components/legalConsentModal/LegalConsentModal';
import { IThreeDSecureDetails } from '../home/models/orderDetailModels';
import { useTranslation } from '../../hooks/useTranslation';
import { RouteConfig } from '../../routeConfig/routeConfig';


// Temporary: Internal test stores for Apple Pay / Google Pay testing (STQR-930). Remove once rolled out broadly.
const WALLET_ENABLED_STORES = process.env.REACT_APP_WALLET_ENABLED_STORES?.split(',') ?? [];

// i4GoUniqueId is set once i4go tokenization succeeds, so the overlay covers pre-auth only and
// the iframe's own loader covers form loading, instead of both showing at the same time.
const getLoaderState = (i4goLoader: boolean, i4GoUniqueId: string) => ({
	isProcessingPayment: i4goLoader && Boolean(i4GoUniqueId),
	isFormLoading: i4goLoader && !i4GoUniqueId,
});

const PreAuthDetails: React.FC = () => {
	const { t } = useTranslation();
	const { storeId } = useParams<{ storeId: string }>();
	const isWalletEnabled = WALLET_ENABLED_STORES.includes(storeId ?? '');
	const {
		tableName,
		getTableName,
		accessDetails,
		getAccessToken,
		accessDetailsError,
		setAccessDetailsError,
		getSessionId,
		sessionIdError,
		setSessionIdError,
	} = useContext(PreAuthPageContext) as IPreAuthItemContext;
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const navigate = useNavigate();
	const tableRefId = localStorage.getItem('tableRef');
	const [i4goLoader, setI4goLoader] = useState<boolean>(false);
	const [isPayWithCard, setIsPayWithCard] = useState<boolean>(!isWalletEnabled);
	const [orderDetail, setOrderDetail] = useState(null);

	const { merchantData } = useContext(MenuItemsPageContext) as IMenuItemContext;
	const { postOrderDetail } = useContext(OrderDetailPageContext) as IOrderDetailContext;
	const { isCheckSessionValid } = useContext(ViewTicketDetailPageContext) as IViewTicketDetailPageContext;
	const [apiErrorMessage, setApiErrorMessage] = useState({ MESSAGE: '', TITLE: '', STATUS: 0 });
	const [i4GoUniqueId, setI4GoUniqueId] = useState<string>('');
	const [i4GoExtendedCardData, setI4GoExtendedCardData] = useState<string>('');
	const [preAuthDetailsError, setPreAuthDetailsError] = useState({ MESSAGE: '', TITLE: '' });
	const [isConsentModalVisible, setIsConsentModalVisible] = useState<boolean>(true);
	const [threeDSecureDetails, setThreeDSecureDetails] = useState<IThreeDSecureDetails | null>(null);
	const [showAdditionalCardFields, setShowAdditionalCardFields] = useState<AdditionalCardFields>();
	const [walletsLoaded, setWalletsLoaded] = useState<WalletsLoaded>({ 'google-pay': false, 'apple-pay': false });

	const TIP_PROPERTY_PREFIX = 'predefinedTipPercentage';
	const tips = merchantData?.tips;

	const defaultTipPercentageValue =
		tips?.preselectedTip &&
		tips.areAllowed &&
		Object.entries(tips).flatMap(([key, value]) => (key.includes(TIP_PROPERTY_PREFIX) ? [value / 100] : []))[
		tips.preselectedTip - 1
		];
	const storedSessionId = localStorage.getItem('storedSessionId');
	const existingSessionId = storedSessionId;

	const order = localStorage.getItem('orderDetail');

	useEffect(() => {
		datadogRum.stopSessionReplayRecording();
		return () => {
			datadogRum.startSessionReplayRecording();
		};
	}, []);

	useEffect(() => {
		if (!tableRefId) {
			localStorage.clear();
			navigate(RouteConfig.scanToStart);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tableRefId])

	useEffect(() => {
		setShowAdditionalCardFields({
			isAvsBillingAddressRequired: merchantData?.isAvsBillingAddressRequired || false,
			isAvsPostalCodeRequired: merchantData?.isAvsPostalCodeRequired || false,
		});
	}, [])

	useEffect(() => {
		if (!existingSessionId && merchantData?.preAuthAmount) {
			const itemInCart = localStorage.getItem('cartItems');
			let totalAmount = 0;
			if (itemInCart) {
				const { totalPrice } = JSON.parse(itemInCart);
				totalAmount = totalPrice;
			}

			const accessDetailsPostData: IAccessDetailsPostData = {
				email: `${localStorage.getItem('customerEmail')}`,
				tips: defaultTipPercentageValue ? Math.round((totalAmount * defaultTipPercentageValue) / 100) : 0,
				totalAmount: merchantData?.preAuthAmount,
			};
			if (storeId) {
				getAccessToken(storeId, accessDetailsPostData);
				getTableName(storeId);
			}
		}

		if (order) {
			setOrderDetail(JSON.parse(order));
			localStorage.setItem('cartItems', JSON.stringify(JSON.parse(order).order));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (i4GoUniqueId) {
			const i4goIdData = {
				i4goUniqueId: i4GoUniqueId,
			};
			setI4goLoader(true);
			if (storeId) {
				getSessionId(storeId, i4goIdData);
			}

		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [i4GoUniqueId]);

	useEffect(() => {
		if (isCheckSessionValid) {
			placeOrder();
		} else if ((tableName && accessDetails.accessBlock) || accessDetailsError) {
			setIsLoading(false);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isCheckSessionValid, orderDetail, tableName, accessDetails, accessDetailsError]);

	useEffect(() => {
		if (sessionIdError) {
			setI4goLoader(false);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sessionIdError]);

	useEffect(() => {
		if (accessDetailsError) {
			setPreAuthDetailsError(getAuthorizeError(t));
		}
		if (sessionIdError) {
			setPreAuthDetailsError(getInvalidCardError(t));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [accessDetailsError, sessionIdError]);

	const handleErrorClick = () => {
		setAccessDetailsError('');
		setSessionIdError('');
		setPreAuthDetailsError({ MESSAGE: '', TITLE: '' });
		navigate(`/${storeId}?tableRef=${tableRefId}`);
	};

	const placeOrder = async () => {
		if (orderDetail && storeId) {
			const orderDetailRequestBody = constructPlaceOrderRequestBody(orderDetail, threeDSecureDetails, i4GoExtendedCardData);
			const orderDetailResponseObject = await postOrderDetail(storeId, orderDetailRequestBody);
			if (orderDetailResponseObject?.error && [500, 400, 404].includes(orderDetailResponseObject.error.status)) {
				localStorage.removeItem('storedSessionId');
				if (orderDetailResponseObject.error.status === 500) {
					setApiErrorMessage(labels.getPreAuthApiError(t, 500));
				} else {
					setPreAuthDetailsError(getInvalidCardError(t));
				}
			} else {
				const selectedPreviousTicketRef = localStorage.getItem('selectedPreviousTicketRef');
				if (!selectedPreviousTicketRef && orderDetailResponseObject.order?.ticketGuid) {
					localStorage.setItem('selectedPreviousTicketRef', orderDetailResponseObject.order.ticketGuid);
				}
				localStorage.removeItem('cartItems');
				setI4goLoader(false);
				navigate(`/${storeId}/OrderConfirmView`);
			}
		}
	};

	const onSuccess = (data: I4goIdResponse) => {
		if (data.i4go_uniqueid && data.i4go_response === 'SUCCESS') {
			if (data.i4go_cardholdername) {
				localStorage.setItem('customerName', data.i4go_cardholdername);
			}
			const threeDSecureData = data?.paymentAPIResults?.['3DS_STANDALONE']?.threeDSecure;
			if (threeDSecureData) {
				setThreeDSecureDetails(threeDSecureData);
			}
			if (data.i4go_extendedcarddata) {
				setI4GoExtendedCardData(data.i4go_extendedcarddata);
			}
			setI4GoUniqueId(data?.i4go_uniqueid);
		}
	};

	const onFailure = (data: I4goIdResponse) => {
		if (data.i4go_response === 'FAILURE') {
			if (data?.i4go_responsecode === '-101') {
				setPreAuthDetailsError(getTimeoutError(t));
			} else {
				setSessionIdError(data?.i4go_responsetext);
			}
		}
	};

	if (preAuthDetailsError.MESSAGE?.length > 0) {
		return (
			<ApiErrorModal
				handleDismiss={handleErrorClick}
				typeOfError={preAuthDetailsError.TITLE}
				errorMsg={preAuthDetailsError.MESSAGE}
				actionType={t('component:apiErrorModal:retry:button')}
				isBackIconEnabled={true}
			/>
		);
	}

	const handleDismiss = () => {
		setIsConsentModalVisible(false);
		localStorage.removeItem('customerEmail');
		navigate(`/${storeId}/orderDetail`, { state: { storeId } });
	};

	const handleConsent = () => {
		setIsConsentModalVisible(false);
	};

	const handlePayWithCard = () => {
		setIsPayWithCard(true);
		setI4goLoader(true);
	};

	const { isProcessingPayment, isFormLoading } = getLoaderState(i4goLoader, i4GoUniqueId);

	return (
		<>
			{isLoading && !accessDetails.accessBlock ? (
				<Spinner />
			) : apiErrorMessage.MESSAGE?.length > 0 ? (
				<ErrorModal
					handleDismiss={handleErrorClick}
					typeOfError={apiErrorMessage.TITLE}
					errorMsg={apiErrorMessage.MESSAGE}
					showButton={true}
				/>
			) : (
				<>
					{merchantData && (
						<LegalConsentModal
							modalHeader={t('component:legalConsent:header:text')}
							showModal={isConsentModalVisible}
							consentMessage={merchantData.preAuthLegalAgreementMessage}
							handleDismiss={handleDismiss}
							handleConsent={handleConsent}
						/>
					)}
					<div className={styles.preAuthContainer}>
						{storeId && (
							<PreAuthHeader storeId={storeId} tableName={tableName} showInfo={i4goLoader && isPayWithCard} isPayWithCard={isPayWithCard}/>
						)}
						{/* Render two versions so the iframe re-mounts and re-initializes when toggling card fields */}
						{!isPayWithCard ? (
							<div className={styles.hideContainer}>
								<CreditCardI4GoIFrame
									onSuccess={onSuccess}
									onFailure={onFailure}
									accessDetails={accessDetails}
									i4goLoader={isFormLoading}
									setI4goLoader={setI4goLoader}
									setWalletsLoaded={setWalletsLoaded}
									showCardFields={false}
									showAdditionalCardField={showAdditionalCardFields}
								/>
							</div>
						) : (
							<CreditCardI4GoIFrame
								onSuccess={onSuccess}
								onFailure={onFailure}
								accessDetails={accessDetails}
								i4goLoader={isFormLoading}
								setI4goLoader={setI4goLoader}
								setWalletsLoaded={setWalletsLoaded}
								showCardFields={true}
								showAdditionalCardField={showAdditionalCardFields}
							/>
						)}
						{!isPayWithCard && (
							<div className={walletsLoaded['google-pay'] || walletsLoaded['apple-pay'] ? styles.walletContainer : styles.hideContainer}>
								<Button type="button" className={styles.payWithCardButton} onClick={handlePayWithCard}>
									Pay with Card
								</Button>
								{/* SDK injection targets — wallets.js injects the Google Pay button inside .google-pay-button
								    and calls .show() on .apple-pay-button directly. */}
								<div className={styles.googlePay}>
									<button className="pay-button google-pay-button hidden"></button>
								</div>
								<div className={styles.applePay}>
									<button className="pay-button pay-hidden apple-pay-button hidden"></button>
								</div>
							</div>
						)}

						{ isWalletEnabled && (!walletsLoaded['google-pay'] && !walletsLoaded['apple-pay']) && (
							<div className={styles.i4goLoaderContainer}>
								<Spinner />
							</div>
						)}
						<PreAuthFooter defaultTipPercentageValue={defaultTipPercentageValue} />
						{/* Blocks the payment method screen while a payment (card, Apple Pay, Google Pay) is being processed */}
						{isProcessingPayment && <Spinner />}
					</div>
				</>
			)}
		</>
	);
};

export default PreAuthDetails;
